/**
 * SubunitSidePanel — Side panel for subunit create + edit in view mode
 *
 * TWO MODES:
 * A) Edit mode: renders SubunitPropertiesPanel with close icon
 * B) Create mode: shows creation form with defaults, ghost follows cursor
 *
 * Rendered as an absolute overlay on the right edge of the map.
 * Follows ObstacleSidePanel pattern.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { X } from 'lucide-react'
import { useSubunitStore, type CreateSubunitDefaults } from '@/stores/subunitStore'
import { SubunitPropertiesPanel } from './SubunitPropertiesPanel'
import {
  SUBUNIT_CATEGORY_LABELS,
  SUBUNIT_CATEGORY_COLORS,
  type SubunitCategory,
  type Subunit,
} from '@/types/subunit'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface SubunitSidePanelProps {
  /** Existing subunit for edit mode (null = create mode) */
  subunit?: Subunit | null
  onClose: () => void
  /** Buildings data for looking up parent unit dimensions */
  buildings?: Building3DConfigExtended[]
}

const CATEGORIES: SubunitCategory[] = ['office', 'storage', 'shop', 'custom']

export function SubunitSidePanel({ subunit, onClose, buildings = [] }: SubunitSidePanelProps) {
  const isCreatingMode = useSubunitStore((s) => s.isCreatingMode)
  const creatingDefaults = useSubunitStore((s) => s.creatingDefaults)
  const cancelCreating = useSubunitStore((s) => s.cancelCreating)
  const setCreatingDefaults = useSubunitStore((s) => s.setCreatingDefaults)

  // Determine mode
  const isCreateMode = isCreatingMode && !subunit

  // Escape key dismisses
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCreateMode) {
          cancelCreating()
        }
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, isCreateMode, cancelCreating])

  // Compute parent unit dimensions for containment clamping.
  // Hoisted above the early returns below so this hook runs on every render
  // (React hooks must be called unconditionally, in the same order each render).
  const parentDimensions = useMemo(() => {
    if (!subunit) return undefined
    for (const b of buildings) {
      const unit = b.generatedUnits?.find(u => u.id === subunit.parentUnitId)
      if (unit) return { width: unit.interior_width, depth: unit.interior_depth }
    }
    return undefined
  }, [subunit?.parentUnitId, buildings])

  if (isCreateMode && creatingDefaults) {
    return (
      <div
        className="absolute right-0 top-0 z-30 w-[420px] border-l bg-background flex flex-col h-full overflow-hidden shadow-lg"
        onWheel={(e) => e.stopPropagation()}
      >
        <CreateSubunitForm
          defaults={creatingDefaults}
          onChange={setCreatingDefaults}
          onCancel={() => {
            cancelCreating()
            onClose()
          }}
        />
      </div>
    )
  }

  if (subunit) {
    return (
      <div
        className="absolute right-0 top-0 z-30 w-[420px] border-l bg-background flex flex-col h-full overflow-hidden shadow-lg"
        onWheel={(e) => e.stopPropagation()}
      >
        <SubunitPropertiesPanel
          subunit={subunit}
          onBack={onClose}
          parentDimensions={parentDimensions}
        />
      </div>
    )
  }

  return null
}

// --------------------------------------------------------------------------
// Create mode form
// --------------------------------------------------------------------------

interface CreateSubunitFormProps {
  defaults: CreateSubunitDefaults
  onChange: (d: CreateSubunitDefaults) => void
  onCancel: () => void
}

function CreateSubunitForm({ defaults, onChange, onCancel }: CreateSubunitFormProps) {
  // Local state mirrors store defaults for responsive UI
  const [form, setForm] = useState<CreateSubunitDefaults>(defaults)

  // Sync local -> store on every change
  const update = useCallback(
    (patch: Partial<CreateSubunitDefaults>) => {
      setForm((prev) => {
        const next = { ...prev, ...patch }
        onChange(next)
        return next
      })
    },
    [onChange]
  )

  // Update color when category changes
  const handleCategoryChange = (category: SubunitCategory) => {
    update({
      category,
      color: SUBUNIT_CATEGORY_COLORS[category],
      name: `New ${SUBUNIT_CATEGORY_LABELS[category]}`,
    })
  }

  // Auto-toggle deductFromParent when elevation changes
  const handleElevationChange = (val: string) => {
    const numVal = parseFloat(val) || 0
    const patch: Partial<CreateSubunitDefaults> = { elevationFt: numVal }
    if (numVal > 0 && form.deductFromParent) {
      patch.deductFromParent = false
    }
    if (numVal === 0 && !form.deductFromParent) {
      patch.deductFromParent = true
    }
    update(patch)
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate">New Subunit</span>
          <span className="text-[10px] text-muted-foreground">Click map to place</span>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-5">
          {/* Identity */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Identity</h4>

            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                className="h-8 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => handleCategoryChange(v as SubunitCategory)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="text-xs">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: SUBUNIT_CATEGORY_COLORS[c] }}
                        />
                        {SUBUNIT_CATEGORY_LABELS[c]}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <Separator />

          {/* Dimensions */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dimensions (ft)</h4>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Width</Label>
                <Input
                  type="number"
                  value={form.width}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v) && v > 0) update({ width: v })
                  }}
                  className="h-8 text-sm"
                  min={1}
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Depth</Label>
                <Input
                  type="number"
                  value={form.depth}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v) && v > 0) update({ depth: v })
                  }}
                  className="h-8 text-sm"
                  min={1}
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Height</Label>
                <Input
                  type="number"
                  value={form.height}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v) && v > 0) update({ height: v })
                  }}
                  className="h-8 text-sm"
                  min={1}
                  step={1}
                />
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Area: {(form.width * form.depth).toLocaleString()} sqft
            </div>
          </section>

          <Separator />

          {/* Elevation */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Elevation</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Elevation (ft)</Label>
                <Input
                  type="number"
                  value={form.elevationFt}
                  onChange={(e) => handleElevationChange(e.target.value)}
                  className="h-8 text-sm"
                  min={0}
                  step={1}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Clearance Buffer (ft)</Label>
                <Input
                  type="number"
                  value={form.clearanceBufferFt}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!isNaN(v)) update({ clearanceBufferFt: v })
                  }}
                  className="h-8 text-sm"
                  min={0}
                  step={0.5}
                />
              </div>
            </div>

            {form.elevationFt > 0 && (
              <p className="text-[10px] text-blue-600">
                Mezzanine level at {form.elevationFt}ft
              </p>
            )}
          </section>

          <Separator />

          {/* Space Deduction */}
          <section className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Space Deduction</h4>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Deduct from parent sqft</Label>
                {form.elevationFt > 0 && !form.deductFromParent && (
                  <p className="text-[10px] text-muted-foreground">(auto: mezzanine)</p>
                )}
              </div>
              <Switch
                checked={form.deductFromParent}
                onCheckedChange={(v) => update({ deductFromParent: v })}
              />
            </div>
          </section>

          <Separator />

          {/* Cancel */}
          <Button variant="outline" size="sm" className="w-full" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </ScrollArea>
    </div>
  )
}
